import { useTranslation } from 'react-i18next';

import { variableTranslations } from '../../constants/variableTranslations';
import { HANDMADE_EVALUATION_TOOL_IDS } from '../../constants/handmadeEvaluationTools';
import { useSharedAssessments } from '../../hooks/useSharedAssessments';
import {
  buildAuthorizedPvqdContextPresentation,
  buildCapeVrResponseUnits,
  buildFeedbackPresentationModel,
} from '../../utils/instrumentResponsePresentation';
import resolveAudioUrl from '../../utils/audio/resolveAudioUrl';
import { ApplicationButton, ApplicationContextualHelp } from '../application';
import AttributeStatsTable from './AttributeStatsTable';
import NumericFeedbackItem from './NumericFeedbackItem';

const GOVERNED_PVQD_REFERENCE_CONTRACT = Object.freeze({
  key: 'pvqd.governed-individual-reference', version: '1',
});

const PROTOCOL_KEY = 'cape-vr';
const PROTOCOL_VERSION = '1.0';
const MINIMUM_N = 5;
const STANDARD_KEYS = Object.freeze(['overallSeverity', 'roughness', 'breathiness', 'strain']);

const labelFor = (unit, t) => t(variableTranslations[unit?.key] || unit?.label || unit?.key, {
  defaultValue: unit?.label || unit?.key,
});

export function buildCapeVrFeedbackData({ learnerModel, directEvidence = [], pvqdContext = [] }) {
  if (
    learnerModel?.kind !== 'normalized' ||
    learnerModel?.protocolKey !== PROTOCOL_KEY ||
    learnerModel?.protocolVersion !== PROTOCOL_VERSION
  ) {
    return { available: false, numericUnits: [], directObservations: [], contextObservations: [] };
  }

  const numericUnits = learnerModel.directResponseUnits.filter(unit =>
    ['numeric', 'customNumeric'].includes(unit.kind) &&
    unit.responseState === 'answered' && Number.isFinite(unit.value)
  );
  const directObservations = directEvidence.flatMap(result => {
    if (learnerModel.assessmentId && result?.assessmentId === learnerModel.assessmentId) return [];
    return STANDARD_KEYS.flatMap(responseUnitKey => {
      const unit = result?.directResponseProjection?.[responseUnitKey];
      return unit?.status === 'answered' && Number.isFinite(unit.value) ? [{
        assessmentId: result.assessmentId || null,
        responseUnitKey,
        value: unit.value,
      }] : [];
    });
  });
  const contextObservations = pvqdContext.flatMap(result => {
    const presentation = buildAuthorizedPvqdContextPresentation(result);
    return presentation.kind === 'authorizedContext'
      ? presentation.observations.filter(unit =>
          STANDARD_KEYS.includes(unit.responseUnitKey) && Number.isFinite(unit.value)
        ).map(unit => ({
          assessmentId: presentation.assessmentId,
          responseUnitKey: unit.responseUnitKey,
          value: unit.value,
        }))
      : [];
  });

  return { available: numericUnits.length > 0, numericUnits, directObservations, contextObservations };
}

const listLabels = (unit, t) => [
  ...(unit?.selected || []).filter(key => key !== 'other').map(key =>
    t(variableTranslations[key] || key, { defaultValue: key })
  ),
  ...(unit?.otherLabel ? [unit.otherLabel] : []),
];

const countBy = values => values.reduce((counts, value) => {
  counts.set(value, (counts.get(value) || 0) + 1);
  return counts;
}, new Map());

const formatCounts = (counts, t) => [...counts.entries()]
  .map(([value, count]) => `${t(value, { defaultValue: value })} ${count}`)
  .join(' · ');

const formatInconsistency = (unit, t) => {
  if (unit?.responseState === 'none') return t('none');
  if (unit?.responseState !== 'present') return null;
  return (unit.affectedTasks || []).map(task =>
    `${t(task.taskKey, { defaultValue: task.taskKey })}: ${task.description}`
  ).join('; ');
};

function CapeVrResponseDetails({ learnerModel, directEvidence, t }) {
  const units = learnerModel.directResponseUnits;
  const categories = units.filter(unit => unit.kind === 'category' && unit.responseState === 'answered');
  const inconsistencies = units.find(unit => unit.kind === 'inconsistency');
  const lists = units.filter(unit => unit.kind === 'reviewedList' && unit.responseState === 'answered');
  const impression = units.find(unit => unit.kind === 'text' && unit.responseState === 'answered');
  const peers = directEvidence.map(result => ({
    assessmentId: result.assessmentId,
    units: buildCapeVrResponseUnits(result.directResponseProjection || {}),
  }));

  const peerUnitsFor = key => peers
    .map(peer => peer.units.find(unit => unit.key === key))
    .filter(Boolean);

  const rows = [
    ...categories.map(unit => {
      const peerUnits = peerUnitsFor(unit.key)
        .filter(peer => peer.responseState === 'answered' && peer.category);
      if (!peerUnits.length) return null;
      const comments = peerUnits.filter(peer => peer.comment).map(peer => peer.comment);
      return {
      key: unit.key,
      label: labelFor(unit, t),
      value: <>{t(unit.category, { defaultValue: unit.category })}{unit.comment ? ` — ${unit.comment}` : ''}</>,
      peers: <>{formatCounts(countBy(peerUnits.map(peer => peer.category)), t)}{comments.length ? (
        <ul className="normalized-feedback-details-table__list">{comments.map((comment, index) => <li key={index}>{comment}</li>)}</ul>
      ) : null}</>,
    };
    }),
    ...(inconsistencies ? [{
      key: 'inconsistencies',
      label: t('inconsistencies'),
      value: formatInconsistency(inconsistencies, t),
      peers: peerUnitsFor('inconsistencies').map(peer => formatInconsistency(peer, t)).filter(Boolean),
    }] : []),
    ...lists.map(unit => {
      const peerUnits = peerUnitsFor(unit.key).filter(peer => peer.responseState === 'answered');
      if (!peerUnits.length) return null;
      const selections = peerUnits.flatMap(peer => listLabels(peer, t).length ? listLabels(peer, t) : [t('none')]);
      return {
      key: unit.key,
      label: labelFor(unit, t),
      value: listLabels(unit, t).join(', ') || t('none'),
      peers: formatCounts(countBy(selections), t),
    };
    }),
    ...(impression ? [{
      key: 'overallImpression',
      label: t('overall_impression'),
      value: impression.comment,
      peers: peerUnitsFor('overallImpression')
        .filter(peer => peer.responseState === 'answered' && peer.comment)
        .map(peer => peer.comment),
    }] : []),
  ].filter(row => row && (Array.isArray(row.peers) ? row.peers.length : row.peers));

  if (!rows.length) return null;

  return (
    <section aria-labelledby="cape-vr-details-heading">
      <h3 id="cape-vr-details-heading" className="feedback-hierarchy__section-title">
        {t('feedback_cape_vr_response_details', { defaultValue: 'Response details' })}
      </h3>
      <div className="normalized-feedback-details-table__container">
        <table className="normalized-feedback-details-table">
          <caption className="feedback-visually-hidden">
            {t('feedback_cape_vr_response_details', { defaultValue: 'Response details' })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('rating_item')}</th>
              <th scope="col">{t('your_response', { defaultValue: 'Your response' })}</th>
              <th scope="col">{t('feedback_shared_allvoiced_responses', { defaultValue: 'Shared All-Voiced responses' })}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                <td>{row.value}</td>
                <td>{Array.isArray(row.peers) ? (
                  <ul className="normalized-feedback-details-table__list">
                    {row.peers.map((value, index) => <li key={index}>{value}</li>)}
                  </ul>
                ) : row.peers}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function CapeVrNormalizedFeedback({
  apiUrl,
  selectedSample,
  userEvaluation,
  onGoToPreview,
  contextLabel,
}) {
  const { t } = useTranslation();
  const learnerModel = buildFeedbackPresentationModel(userEvaluation);
  const {
    directCompatibleEvidence = [],
    pvqdContextObservations = [],
    loading,
    error,
  } = useSharedAssessments(apiUrl, selectedSample?._id, {
    evaluationToolId: HANDMADE_EVALUATION_TOOL_IDS.CAPE_VR,
    protocolKey: learnerModel.protocolKey,
    protocolVersion: learnerModel.protocolVersion,
    pvqdReferenceContract: GOVERNED_PVQD_REFERENCE_CONTRACT,
  });
  const directEvidence = directCompatibleEvidence.filter(result =>
    !learnerModel.assessmentId || result?.assessmentId !== learnerModel.assessmentId
  );
  const data = buildCapeVrFeedbackData({
    learnerModel,
    directEvidence,
    pvqdContext: pvqdContextObservations,
  });

  if (!data.available) return (
    <div className="warning-message" role="status">
      {t('normalized_cape_vr_feedback_unavailable', {
        defaultValue: 'Feedback cannot be shown because this CAPE-Vr response is incomplete.',
      })}
    </div>
  );

  const directHelp = directEvidence.length ? (
    <ApplicationContextualHelp
      label={t('feedback_shared_allvoiced_help_label')}
      title={t('feedback_shared_allvoiced_help_title')}
    >
      <p>{t('feedback_shared_allvoiced_help_body', { toolName: 'CAPE-Vr' })}</p>
    </ApplicationContextualHelp>
  ) : null;
  const pvqdHelp = item => (
    <ApplicationContextualHelp
      label={t('feedback_cape_vr_pvqd_help_label', { defaultValue: 'About the PVQD ratings' })}
      title={t('feedback_cape_vr_pvqd_help_title', { defaultValue: 'About the PVQD ratings' })}
    >
      <p>{t('feedback_cape_vr_pvqd_help_body', {
        item,
        defaultValue: `These ratings come from the Perceptual Voice Qualities Database (PVQD). The points shown in this graph are the ${item} ratings available in PVQD for this same voice sample. PVQD ratings are shown only for Overall Severity, Roughness, Breathiness, and Strain. They provide an additional comparison with your CAPE-Vr rating and do not represent a correct answer. PVQD Pitch and Loudness ratings are not shown because they do not represent CAPE-Vr categories.`,
      })}</p>
    </ApplicationContextualHelp>
  );
  const hasStatistics = data.directObservations.length > 0 || data.contextObservations.length > 0;

  return (
    <div className="feedback-view feedback-hierarchy" data-feedback-protocol={PROTOCOL_KEY}>
      <h2 className="feedback-hierarchy__view-title">{t('feedback')}</h2>
      {contextLabel ? <p className="feedback-hierarchy__metadata">{contextLabel}</p> : null}
      {selectedSample ? (
        <div className="app-module-audio-container">
          <div className="feedback-hierarchy__metadata">
            {t('sample')} {selectedSample.sampleName || selectedSample.name}
          </div>
          {selectedSample.sampleLink ? <audio controls src={resolveAudioUrl(selectedSample.sampleLink)} /> : null}
        </div>
      ) : null}

      <section aria-labelledby="cape-vr-ratings-heading">
        <h3 id="cape-vr-ratings-heading" className="feedback-hierarchy__section-title">
          {t('feedback_os_scale_caption', { defaultValue: 'Ratings on the 0–100 scale' })}
        </h3>
        <div className="normalized-feedback-graphs">
          {data.numericUnits.map(unit => {
            const direct = STANDARD_KEYS.includes(unit.key)
              ? data.directObservations.filter(item => item.responseUnitKey === unit.key) : [];
            const context = STANDARD_KEYS.includes(unit.key)
              ? data.contextObservations.filter(item => item.responseUnitKey === unit.key) : [];
            const label = labelFor(unit, t);
            return (
              <NumericFeedbackItem
                key={unit.key}
                id={`cape-vr-item-${unit.key}`}
                label={label}
                graphProps={{
                  ariaLabel: t('feedback_cape_vr_graph_accessible_name', {
                    item: label,
                    defaultValue: `${label} ratings on the 0–100 scale`,
                  }),
                  userValues: [{ value: unit.value }],
                  sharedValues: [
                    ...direct.map(item => ({ value: item.value, feedbackPresentation: {
                      key: 'cape-vr-direct-compatible',
                      label: t('feedback_shared_allvoiced_ratings'),
                      help: directHelp,
                    } })),
                    ...context.map(item => ({ value: item.value, feedbackPresentation: {
                      role: 'source', key: 'pvqd-context', label: t('feedback_pvqd_context'), help: pvqdHelp(label),
                    } })),
                  ],
                }}
              />
            );
          })}
        </div>
      </section>

      <CapeVrResponseDetails learnerModel={learnerModel} directEvidence={directEvidence} t={t} />
      {loading ? <p>{t('loading_shared_evaluations')}</p> : null}
      {error ? <p className="warning-message" role="status">{t('feedback_cape_vr_comparison_unavailable', {
        defaultValue: 'Your CAPE-Vr response is shown, but comparison information is currently unavailable.',
      })}</p> : null}

      {!loading && !error && hasStatistics ? (
        <section className="feedback-summary-region" aria-labelledby="cape-vr-summary-heading">
          <div className="feedback-summary-region__heading">
            <h3 id="cape-vr-summary-heading" className="feedback-hierarchy__section-title">
              {t('feedback_os_summary_statistics_heading')}
            </h3>
            <ApplicationContextualHelp
              label={t('feedback_os_summary_statistics_help_label')}
              title={t('feedback_os_summary_statistics_help_title')}
            >
              <p>{t('feedback_cape_vr_summary_statistics_help_body', {
                defaultValue: 'When at least 5 ratings are available from the same group for a rating item, All-Voiced shows a summary of those ratings. With fewer ratings, the individual ratings remain visible in the graph. Your own rating is not included, and Shared All-Voiced ratings and PVQD ratings are summarized separately. The mean is the average rating, SD shows how spread out the ratings are, the median is the middle rating, and Q1–Q3 contains the middle half. These statistics do not indicate a correct answer.',
              })}</p>
            </ApplicationContextualHelp>
          </div>
          {[
            ['shared', data.directObservations, t('feedback_shared_allvoiced_ratings'), 'same-instrument-direct'],
            ['pvqd', data.contextObservations, t('feedback_pvqd_context'), 'pvqd-context'],
          ].map(([key, observations, label, populationKind]) => observations.length ? (
            <section key={key} className="feedback-summary-group" aria-labelledby={`cape-vr-${key}-summary`}>
              <h4 id={`cape-vr-${key}-summary`} className="feedback-hierarchy__group-label">{label}</h4>
              <AttributeStatsTable
                mode="numeric-summary"
                summaryVariant="compact-center-spread"
                externalPopulationLabel
                compactPopulationLowCount={key === 'shared'}
                populationLowCountMessage={t('feedback_shared_allvoiced_summary_low_count')}
                observations={observations}
                minimumN={MINIMUM_N}
                populationKind={populationKind}
                caption={label}
                lowCountFormatter={count => count === 1
                  ? t('feedback_os_direct_low_count_one')
                  : t('feedback_os_direct_low_count_other', { count })}
                attributeKeys={STANDARD_KEYS}
              />
            </section>
          ) : null)}
        </section>
      ) : null}

      <div className="app-btn-container">
        <ApplicationButton variant="primary" onClick={onGoToPreview}>
          {t('evaluate_another_voice', 'Evaluate another voice')}
        </ApplicationButton>
      </div>
    </div>
  );
}
